import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Global guard (Feb 2026): every Dialog in the app defaults to non-closing on
// outside-click AND on Escape so operators don't lose half-typed data.
// Any dialog that WANTS the standard shadcn behaviour can pass its own
// `onInteractOutside` / `onEscapeKeyDown` handler and override the guard.
//
// Mar 3, 2026 · a11y auto-fix — Radix requires every DialogContent to be
// paired with a DialogDescription (or `aria-describedby`). Scanning ~70
// call-sites for missing `<DialogDescription>` was flagged by iter-25
// testing_agent. Cheaper + safer to wrap the primitive: if the caller
// omits an `aria-describedby` prop AND doesn't nest an explicit
// `DialogDescription`, we inject a screen-reader-only fallback so the
// dialog remains fully accessible without visual noise.
const _hasDescriptionChild = (children) => {
  let found = false;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) return;
    const t = child.type;
    // Match either our exported wrapper OR the raw Radix primitive.
    if (t === DialogDescription || t === DialogPrimitive.Description) {
      found = true;
      return;
    }
    // Recurse one level so <DialogHeader><DialogDescription/></DialogHeader> counts.
    if (child.props && child.props.children) {
      if (_hasDescriptionChild(child.props.children)) found = true;
    }
  });
  return found;
};

const DialogContent = React.forwardRef(({ className, children, onInteractOutside, onEscapeKeyDown, description, ...props }, ref) => {
  const needsFallbackDescription =
    props["aria-describedby"] == null && !_hasDescriptionChild(children);
  // Mar 3, 2026 · Round 21 · callers can now pass `description="..."` to
  // override the generic fallback with a per-dialog line ("Register a
  // loom on the shop floor" etc.). Still sr-only unless the caller nests
  // their own visible <DialogDescription>.
  const fallbackText = description || "Fill out the form below to save your changes.";
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        onInteractOutside={onInteractOutside ?? ((e) => e.preventDefault())}
        onEscapeKeyDown={onEscapeKeyDown ?? ((e) => e.preventDefault())}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}>
        {needsFallbackDescription && (
          <DialogPrimitive.Description className="sr-only">
            {fallbackText}
          </DialogPrimitive.Description>
        )}
        {children}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
